import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Headset } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { sendChatMessage, getChatSessionId } from '../lib/api';

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm the Xpert Consultant virtual assistant. How can I help with your computer or IT support today?" },
  ]);
  const scrollRef = useRef(null);
  const sessionId = useRef(getChatSessionId());

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, loading]);

  const handleSend = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setInput('');
    setLoading(true);
    try {
      const res = await sendChatMessage(sessionId.current, text);
      setMessages((m) => [...m, { role: 'assistant', content: res.data.reply }]);
    } catch (err) {
      setMessages((m) => [...m, { role: 'assistant', content: "Sorry, I'm having trouble right now. Please call us at 905 909 7555 or email xpertcons1975@gmail.com." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-xl shadow-blue-600/30 flex items-center justify-center transition-all hover:scale-105"
        aria-label="Open chat"
      >
        {open ? <X size={24} /> : <MessageCircle size={26} />}
      </button>

      {/* Window */}
      {open && (
        <div className="fixed bottom-24 right-6 z-[60] w-[92vw] max-w-sm h-[520px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-fade-up">
          <div className="bg-gradient-to-r from-blue-700 to-cyan-600 px-5 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white"><Headset size={20} /></div>
            <div>
              <div className="text-white font-heading font-semibold">Xpert Support</div>
              <div className="text-blue-100 text-xs flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-400 inline-block" /> Online now</div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  m.role === 'user'
                    ? 'bg-blue-600 text-white rounded-br-sm'
                    : 'bg-white text-slate-700 border border-slate-100 rounded-bl-sm shadow-sm'
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-100 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                  <Loader2 size={16} className="animate-spin text-blue-600" />
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSend} className="p-3 border-t border-slate-100 bg-white flex items-center gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 h-11"
            />
            <Button type="submit" disabled={loading} className="h-11 w-11 p-0 bg-blue-600 hover:bg-blue-700 shrink-0">
              <Send size={18} />
            </Button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
